'use client';

import { useEffect, useState, useCallback, useRef } from 'react';

const URL_CHANGE_EVENT = 'nextjsurlchange';

/**
 * A safe way to queue a microtask.
 * If queueMicrotask is not available, use Promise.resolve().then(cb).
 */
const safeQueueMicrotask = typeof queueMicrotask === 'function' ? queueMicrotask : (cb: () => void) => Promise.resolve().then(cb);

export function useSearchParams() {
  const [searchParams, setSearchParams] = useState<Readonly<URLSearchParams> | null>(null);
  const isMounted = useRef<boolean>(false);

  const updateFromURL = useCallback(() => {
        if (globalThis.window === undefined) {

      return;
    }
    const params = new URLSearchParams(window.location.search);

    // 🧩 Defer state update until after hydration commit
    safeQueueMicrotask(() => {
      if (!isMounted.current) {
        return;
      }
      setSearchParams((prev: Readonly<URLSearchParams> | null) => {
        if (prev && prev.toString() === params.toString()) {
          return prev;
        }
        return params;
      })
    })
  }, [])

  useEffect(() => {
    isMounted.current = true;
    updateFromURL();
    return () => {
      isMounted.current = false;
    }
  }, [updateFromURL])

  useEffect(() => {
    const handleUrlChange = () => {
      if (!isMounted.current) {
        return;
      }
      updateFromURL();
    }

    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener(URL_CHANGE_EVENT, handleUrlChange);

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      const result = originalPushState.apply(this, args);
      window.dispatchEvent(new CustomEvent(URL_CHANGE_EVENT));
      return result;
    }

    history.replaceState = function (...args) {
      const result = originalReplaceState.apply(this, args);
      window.dispatchEvent(new CustomEvent(URL_CHANGE_EVENT));
      return result;
    }

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener(URL_CHANGE_EVENT, handleUrlChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    }
  }, [updateFromURL])

  // Comments out this bleow code if we also want to expose the updateSearchParams function

  // const updateSearchParams = useCallback(
  //   (updater: (prev: URLSearchParams) => URLSearchParams) => {
  //     if (!searchParams) {
  //       return;
  //     }
  //     const newParams = updater(new URLSearchParams(searchParams));
  //     const newUrl = `${pathname}?${newParams.toString()}`;
  //     router.push(newUrl, { scroll: false });
  //     // Trigger event manually after push
  //     setTimeout(() => {
  //       window.dispatchEvent(new CustomEvent(URL_CHANGE_EVENT));
  //     }, 0);
  //   },
  //   [router, pathname, searchParams]
  // );

  return searchParams;
}
